import { useState, useEffect } from 'react';

interface TimeSlot {
  start_time: string;
  end_time: string;
}

type AppointmentType = 'evaluation' | 'followup' | 'coaching' | null;

interface BookingCalendarProps {
  timeslotsApi: string;
  appointmentsApi: string;
  texts: {
    selectDate: string;
    chooseTime: string;
    reviewDetails: string;
    fullName: string;
    phoneNumber: string;
    confirmBooking: string;
    modifyTime: string;
    back: string;
    noSlotsAvailable: string;
    bookingSuccess: string;
    bookingError: string;
    loading: string;
  };
  containerStyle?: string;
}

const APPOINTMENT_TYPES = [
  {
    id: 'evaluation' as AppointmentType,
    title: 'ADHD Clarity Evaluation',
    duration: '90 minutes',
    durationMinutes: 90,
    description: 'Comprehensive diagnostic evaluation for new patients. Includes full ADHD assessment, history review, and treatment recommendations.',
    icon: 'ri-mental-health-line',
  },
  {
    id: 'followup' as AppointmentType,
    title: 'Follow-Up Appointment',
    duration: '30 minutes',
    durationMinutes: 30,
    description: 'For existing patients. Covers medication management, symptom review, and ongoing treatment adjustments.',
    icon: 'ri-calendar-check-line',
  },
  {
    id: 'coaching' as AppointmentType,
    title: 'Coaching Intake Call',
    duration: '20 minutes',
    durationMinutes: 20,
    description: 'A brief introductory call to explore ADHD coaching with Lauren. Discuss your goals, challenges, and whether coaching is the right fit for you.',
    icon: 'ri-discuss-line',
  },
];

function getScheduleForDayOfWeek(dayOfWeek: number): { startHour: number; endHour: number } | null {
  if (dayOfWeek >= 1 && dayOfWeek <= 4) return { startHour: 14, endHour: 19 };
  if (dayOfWeek === 5) return null;
  if (dayOfWeek === 0 || dayOfWeek === 6) return { startHour: 9, endHour: 13 };
  return null;
}

function isDateAvailable(dateStr: string): boolean {
  const date = new Date(dateStr + 'T00:00:00');
  return getScheduleForDayOfWeek(date.getDay()) !== null;
}

function generateTimeSlots(date: string, durationMinutes: number): TimeSlot[] {
  const slots: TimeSlot[] = [];
  const dateObj = new Date(date + 'T00:00:00');
  const schedule = getScheduleForDayOfWeek(dateObj.getDay());
  if (!schedule) return [];
  const { startHour, endHour } = schedule;
  for (let h = startHour; h < endHour; h++) {
    for (let m = 0; m < 60; m += 30) {
      const startTotal = h * 60 + m;
      const endTotal = startTotal + durationMinutes;
      if (endTotal > endHour * 60) break;
      const startH = String(Math.floor(startTotal / 60)).padStart(2, '0');
      const startM = String(startTotal % 60).padStart(2, '0');
      const endH = String(Math.floor(endTotal / 60)).padStart(2, '0');
      const endM = String(endTotal % 60).padStart(2, '0');
      slots.push({ start_time: `${date} ${startH}:${startM}:00`, end_time: `${date} ${endH}:${endM}:00` });
    }
  }
  return slots;
}

function formatTime(timeStr: string): string {
  const timePart = timeStr.split(' ')[1]?.substring(0, 5) || timeStr.substring(0, 5);
  const [h, m] = timePart.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${displayH}:${String(m).padStart(2, '0')} ${period}`;
}

const STEPS = ['Type', 'Date', 'Time', 'Your Info', 'Details', 'Confirm'];

export function BookingCalendar({ timeslotsApi, appointmentsApi, texts, containerStyle = '' }: BookingCalendarProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [appointmentType, setAppointmentType] = useState<AppointmentType>(null);
  const [loading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() + 1 };
  });
  const [contactInfo, setContactInfo] = useState({ full_name: '', phone: '', email: '' });
  const [patientDetails, setPatientDetails] = useState({ dob: '', address: '', pharmacy: '' });
  const [contactErrors, setContactErrors] = useState({ full_name: '', phone: '', email: '' });
  const [detailErrors, setDetailErrors] = useState({ dob: '', address: '', pharmacy: '' });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const today = new Date();
    for (let i = 0; i < 60; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const dateStr = d.toISOString().split('T')[0];
      if (isDateAvailable(dateStr)) {
        setSelectedDate(dateStr);
        setCalendarMonth({ year: d.getFullYear(), month: d.getMonth() + 1 });
        break;
      }
    }
  }, []);

  const selectedTypeInfo = APPOINTMENT_TYPES.find(t => t.id === appointmentType);

  const getSlotsForDate = (date: string): TimeSlot[] => {
    if (!appointmentType || !selectedTypeInfo) return [];
    return generateTimeSlots(date, selectedTypeInfo.durationMinutes);
  };

  const getDaysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month - 1, 1).getDay();

  const validateContact = () => {
    const errors = { full_name: '', phone: '', email: '' };
    let valid = true;
    if (!contactInfo.full_name.trim()) { errors.full_name = 'Full name is required.'; valid = false; }
    if (!contactInfo.phone.trim()) { errors.phone = 'Phone number is required.'; valid = false; }
    if (!contactInfo.email.trim()) { errors.email = 'Email address is required.'; valid = false; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) { errors.email = 'Please enter a valid email.'; valid = false; }
    setContactErrors(errors);
    return valid;
  };

  const validateDetails = () => {
    const errors = { dob: '', address: '', pharmacy: '' };
    let valid = true;
    if (!patientDetails.dob.trim()) { errors.dob = 'Date of birth is required.'; valid = false; }
    if (!patientDetails.address.trim()) { errors.address = 'Full address is required.'; valid = false; }
    if (!patientDetails.pharmacy.trim()) { errors.pharmacy = 'Pharmacy preference is required.'; valid = false; }
    setDetailErrors(errors);
    return valid;
  };

  const renderCalendar = () => {
    const { year, month } = calendarMonth;
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const today = new Date().toISOString().split('T')[0];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(<div key={`empty-${i}`} className="h-14" />);
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isPast = dateStr < today;
      const isFriday = new Date(dateStr + 'T00:00:00').getDay() === 5;
      const isAvailable = !isPast && isDateAvailable(dateStr);
      const isSelected = dateStr === selectedDate;
      days.push(
        <button
          key={day}
          onClick={() => { if (isAvailable) setSelectedDate(dateStr); }}
          disabled={!isAvailable}
          title={isFriday ? 'Closed on Fridays' : undefined}
          className={`h-14 rounded-lg text-sm font-medium transition-all duration-200 ${
            isSelected ? 'bg-[#1E2D5E] text-[#FAF7F2] shadow-md cursor-pointer'
            : isAvailable ? 'bg-[#C9A96E]/10 text-[#1E2D5E] hover:bg-[#C9A96E]/20 cursor-pointer'
            : isFriday ? 'bg-transparent text-[#1E2D5E]/20 cursor-not-allowed line-through'
            : 'bg-transparent text-[#1E2D5E]/30 cursor-not-allowed'
          }`}
        >
          <div className="text-xs opacity-60">{dayNames[new Date(year, month - 1, day).getDay()]}</div>
          <div>{day}</div>
        </button>
      );
    }
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => { const nm = month === 1 ? 12 : month - 1; const ny = month === 1 ? year - 1 : year; setCalendarMonth({ year: ny, month: nm }); }} className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#F5F1E8] text-[#1E2D5E] hover:bg-[#C9A96E]/20 transition-colors cursor-pointer">
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <h3 className="text-xl font-medium text-[#1E2D5E] font-['Cormorant_Garamond']">
            {new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <button onClick={() => { const nm = month === 12 ? 1 : month + 1; const ny = month === 12 ? year + 1 : year; setCalendarMonth({ year: ny, month: nm }); }} className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#F5F1E8] text-[#1E2D5E] hover:bg-[#C9A96E]/20 transition-colors cursor-pointer">
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
        <div className="flex items-center gap-4 mb-4 text-xs text-[#1E2D5E]/60">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-[#C9A96E]/20" /><span>Available</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-[#1E2D5E]" /><span>Selected</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-transparent border border-[#1E2D5E]/20" /><span>Unavailable</span></div>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {dayNames.map(name => (
            <div key={name} className={`text-center text-xs font-medium mb-2 ${name === 'Fri' ? 'text-red-300' : 'text-[#1E2D5E]/60'}`}>{name}</div>
          ))}
          {days}
        </div>
        <div className="mt-5 flex items-center gap-2 p-3 bg-[#1E2D5E]/5 rounded-lg border border-[#1E2D5E]/10">
          <i className="ri-time-line text-[#C9A96E] text-base" />
          <p className="text-xs text-[#1E2D5E]/70">All times shown in <strong>Pacific Standard Time (PST)</strong> — Oregon time. Mon–Thu: 2:00–7:00 PM · Sat–Sun: 9:00 AM–1:00 PM · Fri: Closed</p>
        </div>
      </div>
    );
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const response = await fetch(appointmentsApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: contactInfo.full_name,
          customer_phone: contactInfo.phone,
          customer_email: contactInfo.email,
          date_of_birth: patientDetails.dob,
          address: patientDetails.address,
          pharmacy: patientDetails.pharmacy,
          start_time: selectedSlot?.start_time,
          end_time: selectedSlot?.end_time,
          notes: `Appointment type: ${selectedTypeInfo?.title}`,
        }),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: texts.bookingSuccess });
        setContactInfo({ full_name: '', phone: '', email: '' });
        setPatientDetails({ dob: '', address: '', pharmacy: '' });
        setSelectedDate('');
        setSelectedSlot(null);
        setAppointmentType(null);
        setCurrentStep(0);
      } else {
        setMessage({ type: 'error', text: texts.bookingError });
      }
    } catch {
      setMessage({ type: 'error', text: texts.bookingError });
    } finally {
      setSubmitting(false);
    }
  };

  const AppointmentBadge = () => selectedTypeInfo ? (
    <div className="flex items-center gap-3 mb-8 p-4 bg-[#1E2D5E]/5 rounded-xl border border-[#1E2D5E]/10">
      <i className={`${selectedTypeInfo.icon} text-[#C9A96E] text-xl`} />
      <div>
        <span className="text-sm font-medium text-[#1E2D5E]">{selectedTypeInfo.title}</span>
        <span className="text-xs text-[#1E2D5E]/60 ml-2">· {selectedTypeInfo.duration}</span>
      </div>
    </div>
  ) : null;

  const InputField = ({
    label, required, type = 'text', value, onChange, placeholder, error, hint
  }: {
    label: string; required?: boolean; type?: string; value: string;
    onChange: (v: string) => void; placeholder?: string; error?: string; hint?: string;
  }) => (
    <div>
      <label className="block text-sm font-medium text-[#1E2D5E] mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {hint && <p className="text-xs text-[#1E2D5E]/50 mb-2">{hint}</p>}
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-4 py-3 rounded-lg border text-[#1E2D5E] text-sm bg-white focus:outline-none transition-colors ${error ? 'border-red-400 focus:border-red-400' : 'border-[#C9A96E]/30 focus:border-[#C9A96E]'}`}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-20 ${containerStyle}`}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#C9A96E]/30 border-t-[#C9A96E] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#1E2D5E]/70">{texts.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`max-w-4xl mx-auto ${containerStyle}`}>
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2 mb-10 flex-wrap">
        {STEPS.map((label, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="flex flex-col items-center gap-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                i < currentStep ? 'bg-[#C9A96E] text-white' :
                i === currentStep ? 'bg-[#1E2D5E] text-white' :
                'bg-[#E8E2D6] text-[#1E2D5E]/40'
              }`}>
                {i < currentStep ? <i className="ri-check-line text-sm" /> : i + 1}
              </div>
              <span className={`text-xs font-medium whitespace-nowrap ${i === currentStep ? 'text-[#1E2D5E]' : 'text-[#1E2D5E]/40'}`}>{label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`w-8 h-px mb-5 ${i < currentStep ? 'bg-[#C9A96E]' : 'bg-[#E8E2D6]'}`} />}
          </div>
        ))}
      </div>

      {/* Step 0: Appointment Type */}
      {currentStep === 0 && (
        <div className="bg-[#FAF7F2] rounded-2xl p-8 md:p-12 border border-[#C9A96E]/20 shadow-sm">
          <h2 className="text-3xl md:text-4xl font-light text-[#1E2D5E] mb-3 text-center font-['Cormorant_Garamond']">Select Appointment Type</h2>
          <p className="text-center text-[#1E2D5E]/60 text-sm mb-10">Choose the type of appointment that best fits your needs</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {APPOINTMENT_TYPES.map(type => (
              <button key={type.id} onClick={() => setAppointmentType(type.id)}
                className={`text-left p-8 rounded-xl border-2 transition-all duration-200 cursor-pointer ${appointmentType === type.id ? 'border-[#1E2D5E] bg-[#1E2D5E]/5 shadow-md' : 'border-[#C9A96E]/30 bg-white hover:border-[#C9A96E] hover:shadow-sm'}`}>
                <div className="flex flex-col gap-4">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0 ${appointmentType === type.id ? 'bg-[#1E2D5E] text-[#FAF7F2]' : 'bg-[#C9A96E]/10 text-[#C9A96E]'}`}>
                    <i className={`${type.icon} text-xl`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-medium text-[#1E2D5E] mb-1 font-['Cormorant_Garamond']">{type.title}</h3>
                    <span className={`inline-block text-xs font-semibold tracking-wide px-3 py-1 rounded-full mb-3 ${appointmentType === type.id ? 'bg-[#C9A96E] text-white' : 'bg-[#C9A96E]/15 text-[#C9A96E]'}`}>{type.duration}</span>
                    <p className="text-sm text-[#1E2D5E]/70 leading-relaxed">{type.description}</p>
                  </div>
                </div>
                {appointmentType === type.id && (
                  <div className="mt-4 flex items-center gap-2 text-[#1E2D5E]">
                    <i className="ri-checkbox-circle-fill text-[#C9A96E] text-lg" />
                    <span className="text-sm font-medium">Selected</span>
                  </div>
                )}
              </button>
            ))}
          </div>
          <div className="flex justify-end">
            <button onClick={() => setCurrentStep(1)} disabled={!appointmentType}
              className="px-8 py-3 bg-[#1E2D5E] text-[#FAF7F2] rounded-full text-sm font-medium tracking-wide hover:bg-[#2A3F7A] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
              Select Date →
            </button>
          </div>
        </div>
      )}

      {/* Step 1: Select Date */}
      {currentStep === 1 && (
        <div className="bg-[#FAF7F2] rounded-2xl p-8 md:p-12 border border-[#C9A96E]/20 shadow-sm">
          <AppointmentBadge />
          <h2 className="text-3xl md:text-4xl font-light text-[#1E2D5E] mb-8 text-center font-['Cormorant_Garamond']">{texts.selectDate}</h2>
          {renderCalendar()}
          <div className="mt-8 flex justify-between">
            <button onClick={() => setCurrentStep(0)} className="px-8 py-3 bg-[#F5F1E8] text-[#1E2D5E] rounded-full text-sm font-medium tracking-wide hover:bg-[#C9A96E]/20 transition-all duration-300 whitespace-nowrap cursor-pointer">{texts.back}</button>
            <button onClick={() => { if (selectedDate) setCurrentStep(2); }} disabled={!selectedDate}
              className="px-8 py-3 bg-[#1E2D5E] text-[#FAF7F2] rounded-full text-sm font-medium tracking-wide hover:bg-[#2A3F7A] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
              {texts.chooseTime} →
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Choose Time */}
      {currentStep === 2 && (
        <div className="bg-[#FAF7F2] rounded-2xl p-8 md:p-12 border border-[#C9A96E]/20 shadow-sm">
          <AppointmentBadge />
          <h2 className="text-3xl md:text-4xl font-light text-[#1E2D5E] mb-4 text-center font-['Cormorant_Garamond']">{texts.chooseTime}</h2>
          <p className="text-center text-[#1E2D5E]/70 mb-2">
            {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <p className="text-center text-xs text-[#1E2D5E]/50 mb-8 flex items-center justify-center gap-1">
            <i className="ri-map-pin-time-line" /> All times in Pacific Standard Time (PST)
          </p>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-8 max-h-72 overflow-y-auto pr-1">
            {getSlotsForDate(selectedDate).map((slot, index) => {
              const isSelected = selectedSlot?.start_time === slot.start_time;
              return (
                <button key={index} onClick={() => setSelectedSlot(slot)}
                  className={`p-3 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${isSelected ? 'bg-[#1E2D5E] text-[#FAF7F2] shadow-md' : 'bg-[#F5F1E8] text-[#1E2D5E] hover:bg-[#C9A96E]/20'}`}>
                  {formatTime(slot.start_time)}
                </button>
              );
            })}
          </div>
          {getSlotsForDate(selectedDate).length === 0 && <p className="text-center text-[#1E2D5E]/60 mb-8">{texts.noSlotsAvailable}</p>}
          <div className="flex justify-between">
            <button onClick={() => setCurrentStep(1)} className="px-8 py-3 bg-[#F5F1E8] text-[#1E2D5E] rounded-full text-sm font-medium tracking-wide hover:bg-[#C9A96E]/20 transition-all duration-300 whitespace-nowrap cursor-pointer">{texts.back}</button>
            <button onClick={() => setCurrentStep(3)} disabled={!selectedSlot}
              className="px-8 py-3 bg-[#1E2D5E] text-[#FAF7F2] rounded-full text-sm font-medium tracking-wide hover:bg-[#2A3F7A] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
              Your Info →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Contact Information */}
      {currentStep === 3 && (
        <div className="bg-[#FAF7F2] rounded-2xl p-8 md:p-12 border border-[#C9A96E]/20 shadow-sm">
          <AppointmentBadge />
          <h2 className="text-3xl md:text-4xl font-light text-[#1E2D5E] mb-2 text-center font-['Cormorant_Garamond']">Your Contact Information</h2>
          <p className="text-center text-[#1E2D5E]/60 text-sm mb-8">We'll use this to confirm your appointment and send reminders</p>
          <div className="space-y-5 mb-8">
            <InputField
              label="Full Name" required type="text"
              value={contactInfo.full_name}
              onChange={v => setContactInfo({ ...contactInfo, full_name: v })}
              placeholder="Jane Smith"
              error={contactErrors.full_name}
            />
            <InputField
              label="Phone Number" required type="tel"
              value={contactInfo.phone}
              onChange={v => setContactInfo({ ...contactInfo, phone: v })}
              placeholder="(503) 555-0100"
              error={contactErrors.phone}
            />
            <InputField
              label="Email Address" required type="email"
              value={contactInfo.email}
              onChange={v => setContactInfo({ ...contactInfo, email: v })}
              placeholder="jane@example.com"
              error={contactErrors.email}
            />
          </div>
          <div className="flex justify-between">
            <button onClick={() => setCurrentStep(2)} className="px-8 py-3 bg-[#F5F1E8] text-[#1E2D5E] rounded-full text-sm font-medium tracking-wide hover:bg-[#C9A96E]/20 transition-all duration-300 whitespace-nowrap cursor-pointer">{texts.back}</button>
            <button
              onClick={() => { if (validateContact()) setCurrentStep(4); }}
              className="px-8 py-3 bg-[#1E2D5E] text-[#FAF7F2] rounded-full text-sm font-medium tracking-wide hover:bg-[#2A3F7A] transition-all duration-300 whitespace-nowrap cursor-pointer">
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Patient Details */}
      {currentStep === 4 && (
        <div className="bg-[#FAF7F2] rounded-2xl p-8 md:p-12 border border-[#C9A96E]/20 shadow-sm">
          <AppointmentBadge />
          <h2 className="text-3xl md:text-4xl font-light text-[#1E2D5E] mb-2 text-center font-['Cormorant_Garamond']">Patient Details</h2>
          <p className="text-center text-[#1E2D5E]/60 text-sm mb-8">A few more details to prepare for your appointment</p>
          <div className="space-y-5 mb-8">
            <InputField
              label="Date of Birth" required type="date"
              value={patientDetails.dob}
              onChange={v => setPatientDetails({ ...patientDetails, dob: v })}
              error={detailErrors.dob}
            />
            <div>
              <label className="block text-sm font-medium text-[#1E2D5E] mb-1.5">
                Full Physical Address <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-[#1E2D5E]/50 mb-2">Street address, city, state, ZIP</p>
              <textarea
                value={patientDetails.address}
                onChange={e => setPatientDetails({ ...patientDetails, address: e.target.value })}
                placeholder="123 Main St, Portland, OR 97201"
                rows={3}
                maxLength={500}
                className={`w-full px-4 py-3 rounded-lg border text-[#1E2D5E] text-sm bg-white focus:outline-none transition-colors resize-none ${detailErrors.address ? 'border-red-400 focus:border-red-400' : 'border-[#C9A96E]/30 focus:border-[#C9A96E]'}`}
              />
              {detailErrors.address && <p className="text-xs text-red-500 mt-1">{detailErrors.address}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1E2D5E] mb-1.5">
                Pharmacy Preference <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-[#1E2D5E]/50 mb-2">Name and location of your preferred pharmacy</p>
              <input
                type="text"
                value={patientDetails.pharmacy}
                onChange={e => setPatientDetails({ ...patientDetails, pharmacy: e.target.value })}
                placeholder="e.g. CVS Pharmacy, 456 Oak Ave, Portland, OR"
                className={`w-full px-4 py-3 rounded-lg border text-[#1E2D5E] text-sm bg-white focus:outline-none transition-colors ${detailErrors.pharmacy ? 'border-red-400 focus:border-red-400' : 'border-[#C9A96E]/30 focus:border-[#C9A96E]'}`}
              />
              {detailErrors.pharmacy && <p className="text-xs text-red-500 mt-1">{detailErrors.pharmacy}</p>}
            </div>
          </div>
          <div className="flex justify-between">
            <button onClick={() => setCurrentStep(3)} className="px-8 py-3 bg-[#F5F1E8] text-[#1E2D5E] rounded-full text-sm font-medium tracking-wide hover:bg-[#C9A96E]/20 transition-all duration-300 whitespace-nowrap cursor-pointer">{texts.back}</button>
            <button
              onClick={() => { if (validateDetails()) setCurrentStep(5); }}
              className="px-8 py-3 bg-[#1E2D5E] text-[#FAF7F2] rounded-full text-sm font-medium tracking-wide hover:bg-[#2A3F7A] transition-all duration-300 whitespace-nowrap cursor-pointer">
              Review Booking →
            </button>
          </div>
        </div>
      )}

      {/* Step 5: Confirm */}
      {currentStep === 5 && selectedSlot && selectedTypeInfo && (
        <div className="bg-[#FAF7F2] rounded-2xl p-8 md:p-12 border border-[#C9A96E]/20 shadow-sm">
          <h2 className="text-3xl md:text-4xl font-light text-[#1E2D5E] mb-2 text-center font-['Cormorant_Garamond']">Confirm Your Booking</h2>
          <p className="text-center text-[#1E2D5E]/60 text-sm mb-8">Please review your details before confirming</p>

          {/* Appointment summary */}
          <div className="bg-[#F5F1E8] rounded-xl p-6 mb-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-[#C9A96E] mb-3">Appointment</h3>
            <div className="flex items-center gap-3 pb-4 border-b border-[#C9A96E]/20">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C9A96E]/15 text-[#C9A96E]">
                <i className={`${selectedTypeInfo.icon} text-lg`} />
              </div>
              <div>
                <p className="text-base font-medium text-[#1E2D5E]">{selectedTypeInfo.title}</p>
                <p className="text-xs text-[#1E2D5E]/60">{selectedTypeInfo.duration}</p>
              </div>
            </div>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-[#1E2D5E]/60 mb-1">Date &amp; Time</p>
                <p className="text-base font-medium text-[#1E2D5E]">
                  {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                <p className="text-sm text-[#1E2D5E]/80">{formatTime(selectedSlot.start_time)} – {formatTime(selectedSlot.end_time)} PST</p>
              </div>
              <button onClick={() => { setSelectedSlot(null); setCurrentStep(1); }} className="text-sm text-[#C9A96E] hover:text-[#B89858] font-medium cursor-pointer whitespace-nowrap">{texts.modifyTime}</button>
            </div>
          </div>

          {/* Contact summary */}
          <div className="bg-[#F5F1E8] rounded-xl p-6 mb-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-[#C9A96E]">Contact Information</h3>
              <button onClick={() => setCurrentStep(3)} className="text-xs text-[#C9A96E] hover:text-[#B89858] font-medium cursor-pointer whitespace-nowrap">Edit</button>
            </div>
            <div className="space-y-2 text-sm text-[#1E2D5E]">
              <div className="flex items-center gap-2"><i className="ri-user-line text-[#C9A96E]" /><span>{contactInfo.full_name}</span></div>
              <div className="flex items-center gap-2"><i className="ri-phone-line text-[#C9A96E]" /><span>{contactInfo.phone}</span></div>
              <div className="flex items-center gap-2"><i className="ri-mail-line text-[#C9A96E]" /><span>{contactInfo.email}</span></div>
            </div>
          </div>

          {/* Patient details summary */}
          <div className="bg-[#F5F1E8] rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-[#C9A96E]">Patient Details</h3>
              <button onClick={() => setCurrentStep(4)} className="text-xs text-[#C9A96E] hover:text-[#B89858] font-medium cursor-pointer whitespace-nowrap">Edit</button>
            </div>
            <div className="space-y-2 text-sm text-[#1E2D5E]">
              <div className="flex items-center gap-2"><i className="ri-cake-line text-[#C9A96E]" /><span>DOB: {patientDetails.dob}</span></div>
              <div className="flex items-start gap-2"><i className="ri-map-pin-line text-[#C9A96E] mt-0.5" /><span>{patientDetails.address}</span></div>
              <div className="flex items-center gap-2"><i className="ri-medicine-bottle-line text-[#C9A96E]" /><span>{patientDetails.pharmacy}</span></div>
            </div>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setCurrentStep(4)} className="px-8 py-3 bg-[#F5F1E8] text-[#1E2D5E] rounded-full text-sm font-medium tracking-wide hover:bg-[#C9A96E]/20 transition-all duration-300 whitespace-nowrap cursor-pointer">{texts.back}</button>
            <button onClick={handleSubmit} disabled={submitting}
              className="px-8 py-3 bg-[#1E2D5E] text-[#FAF7F2] rounded-full text-sm font-medium tracking-wide hover:bg-[#2A3F7A] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
              {submitting ? 'Submitting...' : texts.confirmBooking}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
